<script setup>
import { ref, defineProps, defineEmits, onMounted } from "vue";

defineProps({
  tooth: {
    type: Number,
    required: true,
  },
});

defineEmits(['serviceSelected']);

const serviceCategory = ref(0);
const serviceType = ref(0);
const serviceGroup = ref(0);
const dentalsRegion = ref(0);

const data = ref({
  categories: [],
  types: [],
  groups: [],
  regions: [],
});
const error = ref(null);

function categoryChanged() {
  serviceType.value = 0;
  serviceGroup.value = 0;
  dentalsRegion.value = 0;

  if (0 == serviceCategory.value) {
    return;
  }

  fetchTypes(serviceCategory.value);
}

function typeChanged() {
  serviceGroup.value = 0;
  dentalsRegion.value = 0;

  if (0 == serviceType.value) {
    return;
  }

  fetchGroups(serviceType.value);
}

async function fetchCategories() {
  return fetch(process.env.VUE_APP_API_BASE_URL + "serviceCategories", {
    method: "get",
    headers: {
      "content-type": "application/json",
    },
  })
    .then((r) => {
      if (!r.ok) {
        error.value = new Error(r.statusText);
        error.value.json = r.json();
        throw error;
      }

      return r.json();
    })
    .then((json) => {
      data.value.categories = json.data;
    })
    .catch((e) => {
      error.value = e;
      if (e.json) {
        return e.json.then((json) => {
          error.value.message = json.message;
        });
      }
    });
}

async function fetchTypes(serviceCategoryId) {
  data.value.types = [];

  return fetch(process.env.VUE_APP_API_BASE_URL + "serviceTypes/?serviceCategory=" + serviceCategoryId, {
    method: "get",
    headers: {
      "content-type": "application/json",
    },
  })
    .then((r) => {
      if (!r.ok) {
        error.value = new Error(r.statusText);
        error.value.json = r.json();

        throw error;
      }

      return r.json();
    })
    .then((json) => {
      data.value.types = json.data;
    })
    .catch((e) => {
      error.value = e;
      if (e.json) {
        return e.json.then((json) => {
          error.value.message = json.message;
        });
      }
    });
}

async function fetchGroups(serviceTypeId) {
  return fetch(process.env.VUE_APP_API_BASE_URL + "serviceGroups/?serviceType=" + serviceTypeId, {
    method: "get",
    headers: {
      "content-type": "application/json",
    },
  })
    .then((r) => {
      if (!r.ok) {
        error.value = new Error(r.statusText);
        error.value.json = r.json();
        throw error;
      }

      return r.json();
    })
    .then((json) => {
      data.value.groups = json.data;
    })
    .catch((e) => {
      error.value = e;
      if (e.json) {
        return e.json.then((json) => {
          error.value.message = json.message;
        });
      }
    })
}

function fetchRegions() {
  return;
}

onMounted(() => {
  fetchCategories();
  fetchRegions();
});

</script>

<template>
  <div class="container">
    <div v-if="tooth">
      <p class="mb-4 font-bold">
        Вы выбрали зуб №{{ tooth }}
      </p>
      <ol
        class="relative text-gray-500 border-l border-gray-200"
      >
        <li class="mb-6 ml-6 text-gray-900">
          <span
            class="stepper-step absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white"
            :class="{ 'step-done': serviceCategory }"
          >
            <span v-if="!serviceCategory">1</span>
            <svg
              v-else
              aria-hidden="true"
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <h3 class="font-medium leading-tight mb-2 pt-1">
            Какая услуга вам требуется?
          </h3>
          <p class="text-sm">
            <select
              id="serviceCategory"
              v-model="serviceCategory"
              class="
                bg-gray-50
                border border-gray-300
                text-gray-900 text-sm
                rounded-lg
                focus:ring-blue-500 focus:border-blue-500
                block
                w-full
                p-2.5
              "
              @change="categoryChanged(e)"
            >
              <option
                selected
                disabled
                value="0"
              >
                Выберите категорию услуги...
              </option>
              <option
                v-for="item in data.categories"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </p>
        </li>

        <li
          class="mb-8 ml-6"
          :class="{ 'text-gray-900': serviceCategory }"
        >
          <span
            class="stepper-step absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white"
            :class="{ 'step-done': serviceType }"
          >
            <span v-if="!serviceType">2</span>
            <svg
              v-else
              aria-hidden="true"
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <h3 class="font-medium leading-tight mb-2 pt-1">
            Выберите тип услуги
          </h3>
          <p
            v-if="serviceCategory"
            class="text-sm"
          >
            <select
              id="serviceType"
              v-model="serviceType"
              class="
                bg-gray-50
                border border-gray-300
                text-gray-900 text-sm
                rounded-lg
                focus:ring-blue-500 focus:border-blue-500
                block
                w-full
                p-2.5
              "
              @change="typeChanged(e)"
            >
              <option
                selected
                disabled
                value="0"
              >
                Выберите тип услуги...
              </option>
              <option
                v-for="item in data.types"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </p>
        </li>

        <li
          class="mb-8 ml-6"
          :class="{ 'text-gray-900': serviceType }"
        >
          <span
            class="stepper-step absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white"
            :class="{ 'step-done': serviceGroup }"
          >
            <span v-if="!serviceGroup">3</span>
            <svg
              v-else
              aria-hidden="true"
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <h3 class="font-medium leading-tight mb-2 pt-1">
            Уточните услугу
          </h3>
          <p
            v-if="serviceType"
            class="text-sm"
          >
            <select
              id="serviceGroup"
              v-model="serviceGroup"
              class="
                bg-gray-50
                border border-gray-300
                text-gray-900 text-sm
                rounded-lg
                focus:ring-blue-500 focus:border-blue-500
                block
                w-full
                p-2.5
              "
            >
              <option
                selected
                disabled
                value="0"
              >
                Выберите услугу...
              </option>
              <option
                v-for="item in data.groups"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </p>
        </li>

        <li
          class="mb-8 ml-6"
          :class="{ 'text-gray-900': serviceGroup }"
        >
          <span
            class="stepper-step absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white"
            :class="{ 'step-done': dentalsRegion }"
          >
            <span v-if="!dentalsRegion">4</span>
            <svg
              v-else
              aria-hidden="true"
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <h3 class="font-medium leading-tight mb-2 pt-1">
            Где вам удобно получить услугу?
          </h3>
          <p
            v-if="serviceGroup"
            class="text-sm"
          >
            <select
              id="dentalsRegion"
              v-model="dentalsRegion"
              class="
                bg-gray-50
                border border-gray-300
                text-gray-900 text-sm
                rounded-lg
                focus:ring-blue-500 focus:border-blue-500
                block
                w-full
                p-2.5
              "
            >
              <option
                selected
                disabled
                value="0"
              >
                Выберите округ...
              </option>
              <optgroup label="Москва">
                <option value="99">
                  Любой округ
                </option>
                <option
                  value="1"
                >
                  ЦАО
                </option>
                <option value="2">
                  САО
                </option>
                <option value="3">
                  СВАО
                </option>
                <option value="4">
                  ВАО
                </option>
                <option value="5">
                  ЮВАО
                </option>
                <option value="6">
                  ЮАО
                </option>
                <option value="7">
                  ЮЗАО
                </option>
                <option value="8">
                  ЗАО
                </option>
                <option value="9">
                  СЗАО
                </option>
                <option value="10">
                  Новая Москва
                </option>
              </optgroup>
              <optgroup label="РФ">
                <option value="999">
                  Все регионы
                </option>
              </optgroup>
            </select>
          </p>
        </li>
      </ol>

      <router-link
        v-if="dentalsRegion"
        :to="{
          name: 'search-by-service-and-region',
          params: {
            id: serviceGroup,
            regionId: dentalsRegion
          }
        }"
        class="btn-custom rounded-full text-sm"
      >
        Найти клиники →
      </router-link>
    </div>
  </div>
</template>


<style scoped>
.stepper-step.step-done {
  color: white;
  background-color: #36B37E;
}
</style>
